import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { BaseLayout } from '@/components/layout/BaseLayout';
import { ThemedCard } from '@/components/ui/themed-card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  ShieldIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  EyeIcon,
  UserIcon,
  MailIcon,
  CalendarIcon,
  DollarSignIcon,
  ImageIcon,
  AlertTriangleIcon,
  BarChartIcon,
  TargetIcon,
  UsersIcon,
  PlayIcon,
  TrendingUpIcon,
  SearchIcon,
  CreditCardIcon
} from 'lucide-react';

interface PendingPurchase {
  id: string;
  user_id: string;
  user_name: string;
  user_email: string;
  amount: number;
  credits: number;
  status: string;
  payment_proof_url?: string;
  created_at: string;
}

interface UserProfile {
  id: string;
  email: string;
  role?: string;
  full_name?: string;
}

interface AdminStats {
  active_raffles: number;
  completed_raffles: number;
  draft_raffles: number;
  cancelled_raffles: number;
  total_raffles: number;
  total_activation_revenue: number;
  raffles_last_30_days: number;
  raffles_last_7_days: number;
}

interface AdminRaffle {
  id: string;
  title: string;
  status: string;
  creator_id: string;
  total_spots: number;
  price_per_spot: number;
  created_at: string;
  creator_name?: string;
  creator_email?: string;
  activation_fee?: number;
  activated_at?: string;
  sold_spots?: number;
}

interface AdminUser {
  id: string;
  email: string;
  full_name?: string;
  credits_balance?: number;
  created_at: string;
  total_raffles?: number;
  active_raffles?: number;
  total_spent?: number;
}

export const AdminPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [pendingPurchases, setPendingPurchases] = useState<PendingPurchase[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPurchase, setSelectedPurchase] = useState<PendingPurchase | null>(null);
  const [showProofModal, setShowProofModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [purchaseToReject, setPurchaseToReject] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [adminStats, setAdminStats] = useState<AdminStats | null>(null);
  const [raffles, setRaffles] = useState<AdminRaffle[]>([]);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [activeTab, setActiveTab] = useState<'purchases' | 'raffles' | 'users' | 'stats'>('purchases');
  const [raffleFilter, setRaffleFilter] = useState<'all' | 'active' | 'draft' | 'completed'>('all');
  const [userSearch, setUserSearch] = useState('');

  useEffect(() => {
    fetchUserProfile();
    fetchPendingPurchases();
    fetchAdminStats();
    fetchAllRaffles();
    fetchAllUsers();
  }, [user]);

  const fetchUserProfile = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (error) throw error;
      
      setProfile(data);

      // Check if user is admin
      if (data?.role !== 'admin' && data?.role !== 'moderator') {
        navigate('/');
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      navigate('/');
    }
  };

  const fetchPendingPurchases = async () => {
    try {
      setLoading(true);
      
      // Fetch all pending purchases
      const { data: purchases, error: purchaseError } = await supabase
        .from('credit_purchases')
        .select('*')
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (purchaseError) throw purchaseError;

      // For each purchase, fetch the user profile and generate signed URL for proof
      const formattedData = await Promise.all((purchases || []).map(async (purchase) => {
        const { data: profile } = await supabase
          .from('profiles')
          .select('full_name, email')
          .eq('id', purchase.user_id)
          .single();

        // If there's a payment proof, generate a signed URL
        let signedProofUrl = null;
        if (purchase.payment_proof_url) {
          // Check if it's already a full URL or just a path
          if (purchase.payment_proof_url.startsWith('http')) {
            signedProofUrl = purchase.payment_proof_url;
          } else {
            // Generate a signed URL valid for 1 hour
            const { data: signedUrlData } = await supabase.storage
              .from('payment-proofs')
              .createSignedUrl(purchase.payment_proof_url, 3600);
            
            signedProofUrl = signedUrlData?.signedUrl || null;
          }
        }

        return {
          ...purchase,
          payment_proof_url: signedProofUrl, // Use the signed URL
          user_name: profile?.full_name || 'Unknown',
          user_email: profile?.email || 'Email não disponível'
        };
      }));

      setPendingPurchases(formattedData);
    } catch (error) {
      console.error('Error fetching pending purchases:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (purchaseId: string) => {
    setActionLoading(true);
    try {
      // Update purchase status to approved
      const { error: updateError } = await supabase
        .from('credit_purchases')
        .update({
          status: 'approved',
          approved_by: user?.id,
          approved_at: new Date().toISOString()
        })
        .eq('id', purchaseId);

      if (updateError) throw updateError;

      // The trigger will automatically update the user's credits

      setSuccessMessage('Compra aprovada com sucesso! Os créditos foram adicionados à conta do usuário.');
      setShowSuccessDialog(true);
      setShowProofModal(false);
      setSelectedPurchase(null);
      fetchPendingPurchases();
    } catch (error) {
      console.error('Error approving purchase:', error);
      setErrorMessage('Erro ao aprovar compra. Tente novamente.');
      setShowErrorDialog(true);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (purchaseId: string) => {
    setPurchaseToReject(purchaseId);
    setShowRejectDialog(true);
  };

  const confirmReject = async () => {
    if (!purchaseToReject) return;

    setActionLoading(true);
    try {
      const { error } = await supabase
        .from('credit_purchases')
        .update({
          status: 'rejected',
          approved_by: user?.id,
          approved_at: new Date().toISOString()
        })
        .eq('id', purchaseToReject);

      if (error) throw error;

      setSuccessMessage('Compra rejeitada.');
      setShowSuccessDialog(true);
      setShowRejectDialog(false);
      setShowProofModal(false);
      setSelectedPurchase(null);
      setPurchaseToReject(null);
      fetchPendingPurchases();
    } catch (error) {
      console.error('Error rejecting purchase:', error);
      setErrorMessage('Erro ao rejeitar compra. Tente novamente.');
      setShowErrorDialog(true);
    } finally {
      setActionLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const fetchAdminStats = async () => {
    try {
      const { data, error } = await supabase
        .from('admin_raffle_stats')
        .select('*')
        .single();

      if (error) throw error;
      setAdminStats(data);
    } catch (error) {
      console.error('Error fetching admin stats:', error);
    }
  };

  const fetchAllRaffles = async () => {
    try {
      // First get all raffles
      const { data: raffles, error: raffleError } = await supabase
        .from('raffles')
        .select('*')
        .order('created_at', { ascending: false });

      if (raffleError) throw raffleError;

      // Then get creator data for each raffle
      const formattedRaffles = await Promise.all(
        (raffles || []).map(async (raffle: any) => {
          const { data: profile, error: profileError } = await supabase
            .from('profiles')
            .select('full_name, email')
            .eq('id', raffle.creator_id)
            .single();

          return {
            ...raffle,
            creator_name: profile?.full_name || 'Usuário Desconhecido',
            creator_email: profile?.email || ''
          };
        })
      );

      setRaffles(formattedRaffles);
    } catch (error) {
      console.error('Error fetching raffles:', error);
    }
  };

  const fetchAllUsers = async () => {
    try {
      // Get users from profiles table with email from auth metadata
      const { data: profiles, error: profileError } = await supabase
        .from('profiles')
        .select('id, full_name, created_at, credits_balance, email')
        .order('created_at', { ascending: false });

      if (profileError) throw profileError;

      const formattedUsers = (profiles || []).map(profile => ({
        ...profile,
        email: profile.email || 'Email não disponível'
      }));

      setUsers(formattedUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const getFilteredRaffles = () => {
    if (raffleFilter === 'all') return raffles;
    return raffles.filter(r => r.status === raffleFilter);
  };

  const getFilteredUsers = () => {
    if (!userSearch) return users;
    return users.filter(user => 
      user.full_name?.toLowerCase().includes(userSearch.toLowerCase()) ||
      user.email.toLowerCase().includes(userSearch.toLowerCase())
    );
  };

  // Loading state while checking user profile
  if (!user || (profile && profile.role !== 'admin' && profile.role !== 'moderator')) {
    // Non-admin users are redirected in useEffect
    return null;
  }

  return (
    <BaseLayout variant="wide">
      <div className="space-y-8">
        {/* Header */}
        <div className="text-center py-8">
          <h1 className="text-4xl font-bold text-foreground mb-4 flex items-center justify-center gap-3">
            <ShieldIcon className="h-10 w-10 text-primary" />
            Painel Administrativo
          </h1>
          <p className="text-xl text-muted-foreground">
            Gerencie rifas, usuários e aprovações do sistema
          </p>
        </div>

        {/* Navigation Tabs */}
        <div className="flex justify-center mb-8">
          <div className="flex space-x-1 bg-muted p-1 rounded-lg">
            <button
              onClick={() => setActiveTab('stats')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'stats'
                  ? 'bg-background text-foreground shadow'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <BarChartIcon className="h-4 w-4 mr-2 inline" />
              Estatísticas
            </button>
            <button
              onClick={() => setActiveTab('raffles')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'raffles'
                  ? 'bg-background text-foreground shadow'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <TargetIcon className="h-4 w-4 mr-2 inline" />
              Rifas
            </button>
            <button
              onClick={() => setActiveTab('users')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'users'
                  ? 'bg-background text-foreground shadow'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <UsersIcon className="h-4 w-4 mr-2 inline" />
              Usuários
            </button>
            <button
              onClick={() => setActiveTab('purchases')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'purchases'
                  ? 'bg-background text-foreground shadow'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <ClockIcon className="h-4 w-4 mr-2 inline" />
              Compras Pendentes
            </button>
          </div>
        </div>


        {/* Admin Statistics */}
        {activeTab === 'stats' && adminStats && (
          <div className="space-y-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <ThemedCard className="p-6 bg-gradient-to-br from-blue-500/10 to-indigo-600/10 border-blue-500/20">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-600 text-sm font-medium mb-1">Total de Rifas</p>
                    <p className="text-3xl font-bold text-foreground">{adminStats.total_raffles}</p>
                  </div>
                  <TargetIcon className="h-8 w-8 text-blue-600" />
                </div>
              </ThemedCard>

              <ThemedCard className="p-6 bg-gradient-to-br from-green-500/10 to-emerald-600/10 border-green-500/20">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-green-600 text-sm font-medium mb-1">Rifas Ativas</p>
                    <p className="text-3xl font-bold text-foreground">{adminStats.active_raffles}</p>
                  </div>
                  <PlayIcon className="h-8 w-8 text-green-600" />
                </div>
              </ThemedCard>

              <ThemedCard className="p-6 bg-gradient-to-br from-purple-500/10 to-violet-600/10 border-purple-500/20">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-purple-600 text-sm font-medium mb-1">Receita de Ativações</p>
                    <p className="text-3xl font-bold text-foreground">{formatCurrency(adminStats.total_activation_revenue)}</p>
                  </div>
                  <DollarSignIcon className="h-8 w-8 text-purple-600" />
                </div>
              </ThemedCard>

              <ThemedCard className="p-6 bg-gradient-to-br from-orange-500/10 to-red-600/10 border-orange-500/20">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-orange-600 text-sm font-medium mb-1">Últimos 30 dias</p>
                    <p className="text-3xl font-bold text-foreground">{adminStats.raffles_last_30_days}</p>
                  </div>
                  <TrendingUpIcon className="h-8 w-8 text-orange-600" />
                </div>
              </ThemedCard>
            </div>
          </div>
        )}

        {/* Raffles Management */}
        {activeTab === 'raffles' && (
          <div className="space-y-6">
            <ThemedCard className="p-8">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-semibold text-foreground">Gerenciar Rifas</h2>
                <div className="flex gap-2">
                  <Button 
                    variant={raffleFilter === 'all' ? 'default' : 'outline'}
                    onClick={() => setRaffleFilter('all')}
                    size="sm"
                  >
                    Todas
                  </Button>
                  <Button 
                    variant={raffleFilter === 'active' ? 'default' : 'outline'}
                    onClick={() => setRaffleFilter('active')}
                    size="sm"
                  >
                    Ativas
                  </Button>
                  <Button 
                    variant={raffleFilter === 'draft' ? 'default' : 'outline'}
                    onClick={() => setRaffleFilter('draft')}
                    size="sm"
                  >
                    Rascunho
                  </Button>
                  <Button 
                    variant={raffleFilter === 'completed' ? 'default' : 'outline'}
                    onClick={() => setRaffleFilter('completed')}
                    size="sm"
                  >
                    Finalizadas
                  </Button>
                </div>
              </div>

              <div className="space-y-4">
                {getFilteredRaffles().map((raffle) => (
                  <ThemedCard key={raffle.id} className="p-6">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <h3 className="text-lg font-semibold text-foreground">{raffle.title}</h3>
                          <Badge 
                            variant={raffle.status === 'active' ? 'default' : 
                                    raffle.status === 'completed' ? 'secondary' : 
                                    raffle.status === 'draft' ? 'outline' : 'destructive'}
                          >
                            {raffle.status}
                          </Badge>
                        </div>
                        <div className="grid md:grid-cols-2 gap-4 text-sm">
                          <div className="space-y-1">
                            <p><span className="text-muted-foreground">Criador:</span> {raffle.creator_name}</p>
                            <p><span className="text-muted-foreground">Email:</span> {raffle.creator_email}</p>
                            <p><span className="text-muted-foreground">Criado em:</span> {formatDate(raffle.created_at)}</p>
                          </div>
                          <div className="space-y-1">
                            <p><span className="text-muted-foreground">Cotas:</span> {raffle.total_spots}</p>
                            <p><span className="text-muted-foreground">Valor por cota:</span> {formatCurrency(raffle.price_per_spot)}</p>
                            <p><span className="text-muted-foreground">Valor total:</span> {formatCurrency(raffle.total_spots * raffle.price_per_spot)}</p>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/raffle/${raffle.id}`)}
                        >
                          <EyeIcon className="h-4 w-4 mr-1" />
                          Ver
                        </Button>
                      </div>
                    </div>
                  </ThemedCard>
                ))}
              </div>
            </ThemedCard>
          </div>
        )}

        {/* Users Management */}
        {activeTab === 'users' && (
          <div className="space-y-6">
            <ThemedCard className="p-8">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-semibold text-foreground">Gerenciar Usuários</h2>
                <div className="relative">
                  <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="Buscar usuário..."
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              <div className="space-y-4">
                {getFilteredUsers().map((user) => (
                  <ThemedCard key={user.id} className="p-6">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <UserIcon className="h-5 w-5 text-muted-foreground" />
                          <h3 className="text-lg font-semibold text-foreground">
                            {user.full_name || 'Nome não informado'}
                          </h3>
                        </div>
                        <div className="grid md:grid-cols-3 gap-4 text-sm">
                          <div className="space-y-1">
                            <p><span className="text-muted-foreground">Email:</span> {user.email}</p>
                            <p><span className="text-muted-foreground">Cadastrado em:</span> {formatDate(user.created_at)}</p>
                          </div>
                          <div className="space-y-1">
                            <p className="flex items-center gap-2">
                              <CreditCardIcon className="h-4 w-4 text-green-600" />
                              <span className="text-muted-foreground">Saldo:</span> 
                              <span className="font-semibold text-green-600">
                                {formatCurrency(user.credits_balance || 0)}
                              </span>
                            </p>
                          </div>
                          <div className="space-y-1">
                            <p><span className="text-muted-foreground">Total de rifas:</span> {user.total_raffles || 0}</p>
                            <p><span className="text-muted-foreground">Rifas ativas:</span> {user.active_raffles || 0}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </ThemedCard>
                ))}
              </div>
            </ThemedCard>
          </div>
        )}

        {/* Pending Purchases */}
        {activeTab === 'purchases' && (
          <ThemedCard className="p-8">
            <h2 className="text-2xl font-semibold text-foreground mb-6 flex items-center gap-2">
              <ClockIcon className="h-6 w-6" />
              Compras Pendentes de Aprovação
            </h2>

          {loading ? (
            <div className="text-center py-12">
              <div className="spinner mx-auto mb-4"></div>
              <p className="text-muted-foreground">Carregando...</p>
            </div>
          ) : pendingPurchases.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircleIcon className="h-12 w-12 text-green-600 mx-auto mb-4" />
              <p className="text-lg text-muted-foreground">
                Nenhuma compra pendente de aprovação.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {pendingPurchases.map((purchase) => (
                <ThemedCard key={purchase.id} className="p-4 hover:border-primary/50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="space-y-2">
                      <div className="flex items-center gap-4">
                        <div>
                          <p className="font-semibold text-foreground flex items-center gap-2">
                            <UserIcon className="h-4 w-4" />
                            {purchase.user_name}
                          </p>
                          <p className="text-sm text-muted-foreground flex items-center gap-2">
                            <MailIcon className="h-3 w-3" />
                            {purchase.user_email}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-6 text-sm">
                        <span className="flex items-center gap-1">
                          <DollarSignIcon className="h-4 w-4 text-green-600" />
                          <span className="font-semibold">{formatCurrency(purchase.amount)}</span>
                        </span>
                        <span className="text-muted-foreground">
                          {purchase.credits} créditos
                        </span>
                        <span className="flex items-center gap-1 text-muted-foreground">
                          <CalendarIcon className="h-3 w-3" />
                          {formatDate(purchase.created_at)}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {purchase.payment_proof_url && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedPurchase(purchase);
                            setShowProofModal(true);
                          }}
                        >
                          <EyeIcon className="h-4 w-4 mr-1" />
                          Ver Comprovante
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleReject(purchase.id)}
                        disabled={actionLoading}
                      >
                        <XCircleIcon className="h-4 w-4 mr-1" />
                        Rejeitar
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleApprove(purchase.id)}
                        disabled={actionLoading}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <CheckCircleIcon className="h-4 w-4 mr-1" />
                        Aprovar
                      </Button>
                    </div>
                  </div>
                </ThemedCard>
              ))}
            </div>
          )}
          </ThemedCard>
        )}
      </div>

      {/* Payment Proof Modal */}
      {showProofModal && selectedPurchase && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-background rounded-lg shadow-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b">
              <h2 className="text-2xl font-semibold text-foreground flex items-center gap-2">
                <ImageIcon className="h-6 w-6" />
                Comprovante de Pagamento
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                {selectedPurchase.user_name} - {formatCurrency(selectedPurchase.amount)}
              </p>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {selectedPurchase.payment_proof_url ? (
                <div className="space-y-4">
                  <img
                    src={selectedPurchase.payment_proof_url}
                    alt="Comprovante"
                    className="w-full h-auto rounded-lg border border-border"
                    onError={(e) => {
                      console.error('Error loading image:', selectedPurchase.payment_proof_url);
                      e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgZmlsbD0iIzMzMyIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTgiIGZpbGw9IiM5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5FcnJvIGFvIGNhcnJlZ2FyIGltYWdlbTwvdGV4dD48L3N2Zz4=';
                    }}
                  />
                  {selectedPurchase.payment_proof_url && (
                    <div className="text-xs text-muted-foreground text-center">
                      <p>URL: {selectedPurchase.payment_proof_url}</p>
                      <a 
                        href={selectedPurchase.payment_proof_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        Abrir em nova aba
                      </a>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-12">
                  Nenhum comprovante enviado.
                </p>
              )}
            </div>
            <div className="p-6 border-t flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowProofModal(false);
                  setSelectedPurchase(null);
                }}
              >
                Fechar
              </Button>
              <Button
                variant="destructive"
                onClick={() => handleReject(selectedPurchase.id)}
                disabled={actionLoading}
              >
                <XCircleIcon className="h-4 w-4 mr-1" />
                Rejeitar
              </Button>
              <Button
                onClick={() => handleApprove(selectedPurchase.id)}
                disabled={actionLoading}
                className="bg-green-600 hover:bg-green-700"
              >
                <CheckCircleIcon className="h-4 w-4 mr-1" />
                Aprovar e Adicionar Créditos
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Success Dialog */}
      <Dialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircleIcon className="h-5 w-5 text-green-600" />
              Sucesso
            </DialogTitle>
            <DialogDescription className="pt-2">
              {successMessage}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end">
            <Button onClick={() => setShowSuccessDialog(false)}>
              OK
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reject Confirmation Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangleIcon className="h-5 w-5 text-amber-500" />
              Confirmar Rejeição
            </DialogTitle>
            <DialogDescription className="pt-2">
              Tem certeza que deseja rejeitar esta compra? Esta ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowRejectDialog(false);
                setPurchaseToReject(null);
              }}
            >
              Cancelar
            </Button>
            <Button 
              variant="destructive" 
              onClick={confirmReject}
              disabled={actionLoading}
            >
              {actionLoading ? 'Rejeitando...' : 'Rejeitar Compra'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Error Dialog */}
      <Dialog open={showErrorDialog} onOpenChange={setShowErrorDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <XCircleIcon className="h-5 w-5 text-red-600" />
              Erro
            </DialogTitle>
            <DialogDescription className="pt-2">
              {errorMessage}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end">
            <Button onClick={() => setShowErrorDialog(false)}>
              OK
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </BaseLayout>
  );
};